"use client";
import { useReducedMotion } from "@/hooks/useReducedMotion";
/**
 * AuraGlass Self-Healing Glass Components System
 * Automatically detects and corrects visual glitches, accessibility issues, and system errors
 * Part of Next-Wave Systems (10/10) - Meta-Systems Framework
 */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  createContext,
  useContext,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { ContrastGuard } from "../accessibility/ContrastGuard";
import { ANIMATION } from "../../tokens/designConstants";

// Self-healing system types
interface ComponentHealthCheck {
  componentId: string;
  componentType: string;
  healthScore: number;
  issues: ComponentIssue[];
  lastChecked: number;
  recoveryAttempts: number;
  status: "healthy" | "warning" | "critical" | "healing" | "failed";
}

interface ComponentIssue {
  id: string;
  type:
    | "visual-glitch"
    | "accessibility"
    | "performance"
    | "memory-leak"
    | "interaction"
    | "rendering";
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  detectedAt: number;
  autoFixable: boolean;
  fixAttempts: number;
  resolved: boolean;
  metadata: Record<string, unknown>;
}

interface HealingAction {
  id: string;
  targetComponent: string;
  actionType:
    | "style-reset"
    | "dom-rebuild"
    | "state-recovery"
    | "accessibility-fix"
    | "performance-optimization";
  description: string;
  implementation: () => Promise<boolean>;
  rollback: () => Promise<void>;
  successRate: number;
  appliedAt?: number;
}

interface HealingStrategy {
  name: string;
  priority: number;
  conditions: (issue: ComponentIssue) => boolean;
  actions: HealingAction[];
  successRate: number;
  executionTime: number;
}

interface BrowserPerformanceMemory {
  usedJSHeapSize: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: BrowserPerformanceMemory;
}

type SelfHealingComponentInput = Record<string, unknown>;

const HEALTH_STATUS_COLORS: Record<ComponentHealthCheck["status"], string> = {
  healthy: "hsl(var(--glass-color-success))",
  warning: "hsl(var(--glass-color-warning))",
  critical: "hsl(var(--glass-color-danger))",
  healing: "hsl(var(--glass-color-primary))",
  failed: "#7f1d1d",
};

// Visual anomaly detection using computer vision techniques
class VisualAnomalyDetector {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private referenceSnapshots: Map<string, ImageData>;
  private anomalyThreshold: number;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.context = this.canvas.getContext("2d")!;
    this.referenceSnapshots = new Map();
    this.anomalyThreshold = 0.15; // 15% difference threshold
  }

  captureElementSnapshot(element: HTMLElement, id: string): ImageData | null {
    try {
      const rect = element.getBoundingClientRect();
      this.canvas.width = rect.width;
      this.canvas.height = rect.height;

      // Create a more detailed visual representation
      const computedStyles = window.getComputedStyle(element);
      const elementClone = element.cloneNode(true) as HTMLElement;

      // Render element to canvas using html2canvas-like technique
      const serializer = new XMLSerializer();
      const html = serializer.serializeToString(elementClone);
      const blob = new Blob([html], { type: "text/html" });

      // For now, use a simplified approach capturing computed style data
      const snapshot = this.context.createImageData(
        rect.width || 100,
        rect.height || 50
      );
      const styleHash = this.computeStyleHash(computedStyles);

      // Create a visual fingerprint based on element properties
      for (let i = 0; i < snapshot.data.length; i += 4) {
        snapshot.data[i] = (styleHash & 0xff0000) >> 16; // Red
        snapshot.data[i + 1] = (styleHash & 0x00ff00) >> 8; // Green
        snapshot.data[i + 2] = styleHash & 0x0000ff; // Blue
        snapshot.data[i + 3] = 255; // Alpha
      }

      return snapshot;
    } catch {
      return null;
    }
  }

  private computeStyleHash(styles: CSSStyleDeclaration): number {
    const importantProps = [
      "width",
      "height",
      "background",
      "border",
      "opacity",
      "transform",
      "filter",
      "position",
      "top",
      "left",
      "display",
      "visibility",
      "zIndex",
    ];

    let hash = 0;
    importantProps.forEach((prop) => {
      const value = styles.getPropertyValue(prop);
      for (let i = 0; i < value.length; i++) {
        hash = (hash << 5) - hash + value.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
      }
    });

    return Math.abs(hash);
  }

  detectAnomalies(element: HTMLElement, id: string): ComponentIssue[] {
    const issues: ComponentIssue[] = [];
    const currentSnapshot = this.captureElementSnapshot(element, id);

    if (!currentSnapshot) return issues;

    const referenceSnapshot = this.referenceSnapshots.get(id);

    if (referenceSnapshot) {
      const difference = this.calculateImageDifference(
        referenceSnapshot,
        currentSnapshot
      );

      if (difference > this.anomalyThreshold) {
        issues.push({
          id: `visual-anomaly-${Date.now()}`,
          type: "visual-glitch",
          severity:
            difference > 0.5
              ? "critical"
              : difference > 0.3
                ? "high"
                : "medium",
          description: `Visual anomaly detected: ${(difference * 100).toFixed(1)}% difference from reference`,
          detectedAt: Date.now(),
          autoFixable: true,
          fixAttempts: 0,
          resolved: false,
          metadata: { difference, hasReference: true },
        });
      }
    } else {
      // Store as reference if this is the first healthy snapshot
      this.referenceSnapshots.set(id, currentSnapshot);
    }

    // Check for common visual issues
    issues.push(...this.checkCommonVisualIssues(element));

    return issues;
  }

  private calculateImageDifference(img1: ImageData, img2: ImageData): number {
    if (img1.data.length !== img2.data.length) return 1.0;

    let totalDifference = 0;
    const length = img1.data.length;

    for (let i = 0; i < length; i += 4) {
      const rDiff = Math.abs(img1.data[i] - img2.data[i]);
      const gDiff = Math.abs(img1.data[i + 1] - img2.data[i + 1]);
      const bDiff = Math.abs(img1.data[i + 2] - img2.data[i + 2]);
      const aDiff = Math.abs(img1.data[i + 3] - img2.data[i + 3]);

      totalDifference += (rDiff + gDiff + bDiff + aDiff) / (255 * 4);
    }

    return totalDifference / (length / 4);
  }

  private checkCommonVisualIssues(element: HTMLElement): ComponentIssue[] {
    const issues: ComponentIssue[] = [];
    const styles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    // Check for invisible elements
    if (
      styles.opacity === "0" ||
      styles.visibility === "hidden" ||
      styles.display === "none"
    ) {
      issues.push({
        id: `invisible-element-${Date.now()}`,
        type: "visual-glitch",
        severity: "high",
        description: "Element is not visible to users",
        detectedAt: Date.now(),
        autoFixable: true,
        fixAttempts: 0,
        resolved: false,
        metadata: {
          opacity: styles.opacity,
          visibility: styles.visibility,
          display: styles.display,
        },
      });
    }

    // Check for elements outside viewport
    if (
      rect.right < 0 ||
      rect.left > window.innerWidth ||
      rect.bottom < 0 ||
      rect.top > window.innerHeight
    ) {
      issues.push({
        id: `outside-viewport-${Date.now()}`,
        type: "visual-glitch",
        severity: "medium",
        description: "Element is positioned outside the viewport",
        detectedAt: Date.now(),
        autoFixable: true,
        fixAttempts: 0,
        resolved: false,
        metadata: { rect: { ...rect } },
      });
    }

    // Check for extremely small elements
    if (rect.width < 1 || rect.height < 1) {
      issues.push({
        id: `tiny-element-${Date.now()}`,
        type: "visual-glitch",
        severity: "medium",
        description: "Element has collapsed dimensions",
        detectedAt: Date.now(),
        autoFixable: true,
        fixAttempts: 0,
        resolved: false,
        metadata: { width: rect.width, height: rect.height },
      });
    }

    // Check for broken transforms
    const transform = styles.transform;
    if (transform && transform !== "none" && transform.includes("NaN")) {
      issues.push({
        id: `broken-transform-${Date.now()}`,
        type: "visual-glitch",
        severity: "critical",
        description: "CSS transform contains invalid values",
        detectedAt: Date.now(),
        autoFixable: true,
        fixAttempts: 0,
        resolved: false,
        metadata: { transform },
      });
    }

    return issues;
  }

  updateReference(element: HTMLElement, id: string): void {
    const snapshot = this.captureElementSnapshot(element, id);
    if (snapshot) {
      this.referenceSnapshots.set(id, snapshot);
    }
  }
}

// Accessibility anomaly detector
class AccessibilityAnomalyDetector {
  detectIssues(element: HTMLElement): ComponentIssue[] {
    const issues: ComponentIssue[] = [];

    // Check for missing ARIA attributes
    const ariaIssues = this.checkAriaCompliance(element);
    issues.push(...ariaIssues);

    // Check for color contrast issues
    const contrastIssues = this.checkColorContrast(element);
    issues.push(...contrastIssues);

    // Check for keyboard accessibility
    const keyboardIssues = this.checkKeyboardAccessibility(element);
    issues.push(...keyboardIssues);

    // Check for focus management
    const focusIssues = this.checkFocusManagement(element);
    issues.push(...focusIssues);

    return issues;
  }

  private checkAriaCompliance(element: HTMLElement): ComponentIssue[] {
    const issues: ComponentIssue[] = [];

    // Check for interactive elements without proper ARIA
    const interactiveElements = element.querySelectorAll(
      "button, a, input, select, textarea"
    );
    interactiveElements.forEach((el, index) => {
      const htmlEl = el as HTMLElement;

      if (
        !htmlEl.getAttribute("aria-label") &&
        !htmlEl.getAttribute("aria-labelledby") &&
        !htmlEl.textContent?.trim()
      ) {
        issues.push({
          id: `missing-aria-label-${Date.now()}-${index}`,
          type: "accessibility",
          severity: "high",
          description: `Interactive element ${el.tagName.toLowerCase()} lacks accessible name`,
          detectedAt: Date.now(),
          autoFixable: true,
          fixAttempts: 0,
          resolved: false,
          metadata: {
            element: el.tagName.toLowerCase(),
            hasTextContent: !!htmlEl.textContent?.trim(),
          },
        });
      }
    });

    // Check for images without alt text
    const images = element.querySelectorAll("img");
    images.forEach((img, index) => {
      if (!img.getAttribute("alt") && !img.getAttribute("aria-label")) {
        issues.push({
          id: `missing-alt-text-${Date.now()}-${index}`,
          type: "accessibility",
          severity: "high",
          description: "Image missing alternative text",
          detectedAt: Date.now(),
          autoFixable: true,
          fixAttempts: 0,
          resolved: false,
          metadata: { src: img.src },
        });
      }
    });

    return issues;
  }

  private checkColorContrast(element: HTMLElement): ComponentIssue[] {
    const issues: ComponentIssue[] = [];
    const styles = window.getComputedStyle(element);

    const backgroundColor = styles.backgroundColor;
    const color = styles.color;

    if (
      backgroundColor &&
      color &&
      backgroundColor !== "rgba(0, 0, 0, 0)" &&
      backgroundColor !== "transparent"
    ) {
      const contrastRatio = this.calculateContrastRatio(color, backgroundColor);

      if (contrastRatio < 4.5) {
        issues.push({
          id: `low-contrast-${Date.now()}`,
          type: "accessibility",
          severity: contrastRatio < 3 ? "critical" : "high",
          description: `Insufficient color contrast: ${contrastRatio.toFixed(2)}:1`,
          detectedAt: Date.now(),
          autoFixable: true,
          fixAttempts: 0,
          resolved: false,
          metadata: { contrastRatio, color, backgroundColor },
        });
      }
    }

    return issues;
  }

  private calculateContrastRatio(color1: string, color2: string): number {
    // Simplified contrast calculation
    const rgb1 = this.parseColor(color1);
    const rgb2 = this.parseColor(color2);

    const l1 = this.relativeLuminance(rgb1);
    const l2 = this.relativeLuminance(rgb2);

    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);

    return (lighter + 0.05) / (darker + 0.05);
  }

  private parseColor(color: string): [number, number, number] {
    // Simple RGB extraction (works for rgb() and rgba() formats)
    const match = color.match(/rgba?\(([^)]+)\)/);
    if (match) {
      const values = match[1].split(",").map((v) => parseInt(v.trim()));
      return [values[0] || 0, values[1] || 0, values[2] || 0];
    }
    return [0, 0, 0]; // Fallback
  }

  private relativeLuminance([r, g, b]: [number, number, number]): number {
    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;

    const rLin =
      rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gLin =
      gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bLin =
      bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
  }

  private checkKeyboardAccessibility(element: HTMLElement): ComponentIssue[] {
    const issues: ComponentIssue[] = [];

    // Check for interactive elements that aren't keyboard accessible
    const interactiveElements = element.querySelectorAll(
      "div[onclick], span[onclick]"
    );
    interactiveElements.forEach((el, index) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.tabIndex < 0 && !htmlEl.getAttribute("role")) {
        issues.push({
          id: `keyboard-inaccessible-${Date.now()}-${index}`,
          type: "accessibility",
          severity: "high",
          description: "Interactive element not keyboard accessible",
          detectedAt: Date.now(),
          autoFixable: true,
          fixAttempts: 0,
          resolved: false,
          metadata: {
            tagName: el.tagName.toLowerCase(),
            hasOnClick: !!htmlEl.onclick,
          },
        });
      }
    });

    return issues;
  }

  private checkFocusManagement(element: HTMLElement): ComponentIssue[] {
    const issues: ComponentIssue[] = [];

    // Check for focus traps without proper management
    const focusableElements = element.querySelectorAll(
      'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 10) {
      // Large number of focusable elements might need focus management
      let hasFocusManagement = false;

      focusableElements.forEach((el) => {
        if (
          el.getAttribute("aria-describedby") ||
          el.getAttribute("aria-controls")
        ) {
          hasFocusManagement = true;
        }
      });

      if (!hasFocusManagement) {
        issues.push({
          id: `focus-management-missing-${Date.now()}`,
          type: "accessibility",
          severity: "medium",
          description: "Complex interface may need better focus management",
          detectedAt: Date.now(),
          autoFixable: false,
          fixAttempts: 0,
          resolved: false,
          metadata: { focusableCount: focusableElements.length },
        });
      }
    }

    return issues;
  }
}

// Performance anomaly detector
class PerformanceAnomalyDetector {
  private renderTimes: Map<string, number[]>;
  private memoryUsage: Map<string, number[]>;

  constructor() {
    this.renderTimes = new Map();
    this.memoryUsage = new Map();
  }

  recordRenderTime(componentId: string, time: number): void {
    if (!this.renderTimes.has(componentId)) {
      this.renderTimes.set(componentId, []);
    }
    const times = this.renderTimes.get(componentId)!;
    times.push(time);

    // Keep only recent measurements
    if (times.length > 50) {
      times.splice(0, times.length - 50);
    }
  }

  detectPerformanceIssues(componentId: string): ComponentIssue[] {
    const issues: ComponentIssue[] = [];
    const renderTimes = this.renderTimes.get(componentId);

    if (!renderTimes || renderTimes.length < 5) return issues;

    const avgRenderTime =
      renderTimes.reduce((sum, time) => sum + time, 0) / renderTimes.length;
    const maxRenderTime = Math.max(...renderTimes);

    // Check for slow rendering
    if (avgRenderTime > 16.67) {
      // 60fps threshold
      issues.push({
        id: `slow-render-${Date.now()}`,
        type: "performance",
        severity:
          avgRenderTime > 33.33
            ? "critical"
            : avgRenderTime > 25
              ? "high"
              : "medium",
        description: `Slow rendering detected: ${avgRenderTime.toFixed(2)}ms average`,
        detectedAt: Date.now(),
        autoFixable: true,
        fixAttempts: 0,
        resolved: false,
        metadata: {
          avgRenderTime,
          maxRenderTime,
          sampleCount: renderTimes.length,
        },
      });
    }

    // Check for rendering spikes
    const recentSpikes = renderTimes.filter((time) => time > avgRenderTime * 2);
    if (recentSpikes.length > renderTimes.length * 0.1) {
      issues.push({
        id: `render-spikes-${Date.now()}`,
        type: "performance",
        severity: "high",
        description: `Frequent rendering spikes detected: ${recentSpikes.length} spikes`,
        detectedAt: Date.now(),
        autoFixable: true,
        fixAttempts: 0,
        resolved: false,
        metadata: { spikes: recentSpikes.length, threshold: avgRenderTime * 2 },
      });
    }

    // Check memory usage if available
    const memory = (performance as PerformanceWithMemory).memory;
    if (memory) {
      const memUsage = memory.usedJSHeapSize / 1024 / 1024;
      if (memUsage > 50) {
        // 50MB threshold
        issues.push({
          id: `high-memory-${Date.now()}`,
          type: "memory-leak",
          severity: memUsage > 100 ? "critical" : "high",
          description: `High memory usage: ${memUsage.toFixed(1)}MB`,
          detectedAt: Date.now(),
          autoFixable: false,
          fixAttempts: 0,
          resolved: false,
          metadata: { memoryUsage: memUsage },
        });
      }
    }

    return issues;
  }
}

// Main self-healing system
class SelfHealingSystem {
  private visualDetector: VisualAnomalyDetector;
  private a11yDetector: AccessibilityAnomalyDetector;
  private perfDetector: PerformanceAnomalyDetector;
  private componentHealth: Map<string, ComponentHealthCheck>;
  private healingStrategies: HealingStrategy[];
  private activeHealing: Map<string, HealingAction[]>;

  constructor() {
    this.visualDetector = new VisualAnomalyDetector();
    this.a11yDetector = new AccessibilityAnomalyDetector();
    this.perfDetector = new PerformanceAnomalyDetector();
    this.componentHealth = new Map();
    this.healingStrategies = this.initializeHealingStrategies();
    this.activeHealing = new Map();
  }

  private initializeHealingStrategies(): HealingStrategy[] {
    return [
      {
        name: "Visual Glitch Recovery",
        priority: 1,
        conditions: (issue) => issue.type === "visual-glitch",
        actions: [
          {
            id: "style-reset",
            targetComponent: "",
            actionType: "style-reset",
            description: "Reset component styles to default state",
            implementation: async () => this.resetComponentStyles(),
            rollback: async () => this.restoreComponentStyles(),
            successRate: 0.85,
          },
          {
            id: "dom-rebuild",
            targetComponent: "",
            actionType: "dom-rebuild",
            description: "Rebuild component DOM structure",
            implementation: async () => this.rebuildComponentDOM(),
            rollback: async () => this.restoreComponentDOM(),
            successRate: 0.75,
          },
        ],
        successRate: 0.8,
        executionTime: 500,
      },
      {
        name: "Accessibility Fix",
        priority: 2,
        conditions: (issue) => issue.type === "accessibility",
        actions: [
          {
            id: "aria-fix",
            targetComponent: "",
            actionType: "accessibility-fix",
            description: "Apply automatic ARIA attributes",
            implementation: async () => this.fixAriaAttributes(),
            rollback: async () => this.removeAriaAttributes(),
            successRate: 0.95,
          },
        ],
        successRate: 0.9,
        executionTime: 200,
      },
      {
        name: "Performance Optimization",
        priority: 3,
        conditions: (issue) => issue.type === "performance",
        actions: [
          {
            id: "perf-optimize",
            targetComponent: "",
            actionType: "performance-optimization",
            description: "Apply performance optimizations",
            implementation: async () => this.optimizePerformance(),
            rollback: async () => this.revertPerformanceChanges(),
            successRate: 0.7,
          },
        ],
        successRate: 0.65,
        executionTime: 1000,
      },
    ];
  }

  async diagnoseComponent(
    element: HTMLElement,
    componentId: string,
    componentType: string
  ): Promise<ComponentHealthCheck> {
    const startTime = performance.now();

    // Collect issues from all detectors
    const visualIssues = this.visualDetector.detectAnomalies(
      element,
      componentId
    );
    const a11yIssues = this.a11yDetector.detectIssues(element);
    const perfIssues = this.perfDetector.detectPerformanceIssues(componentId);

    const allIssues = [...visualIssues, ...a11yIssues, ...perfIssues];

    // Calculate health score
    const healthScore = this.calculateHealthScore(allIssues);

    // Determine status
    let status: ComponentHealthCheck["status"] = "healthy";
    if (allIssues.some((i) => i.severity === "critical")) status = "critical";
    else if (allIssues.some((i) => i.severity === "high")) status = "warning";

    const diagnosis: ComponentHealthCheck = {
      componentId,
      componentType,
      healthScore,
      issues: allIssues,
      lastChecked: Date.now(),
      recoveryAttempts:
        this.componentHealth.get(componentId)?.recoveryAttempts || 0,
      status,
    };

    this.componentHealth.set(componentId, diagnosis);

    // Record performance
    const diagnosisTime = performance.now() - startTime;
    this.perfDetector.recordRenderTime(
      `diagnosis-${componentId}`,
      diagnosisTime
    );

    // Auto-heal if necessary
    if (status === "critical" || (status === "warning" && healthScore < 0.7)) {
      await this.initiateHealing(componentId, element);
    }

    return diagnosis;
  }

  private calculateHealthScore(issues: ComponentIssue[]): number {
    if (issues.length === 0) return 1.0;

    const severityWeights = { low: 0.1, medium: 0.3, high: 0.6, critical: 1.0 };
    const totalImpact = issues.reduce(
      (sum, issue) => sum + severityWeights[issue.severity],
      0
    );
    const maxPossibleImpact = issues.length * 1.0;

    return Math.max(0, 1 - totalImpact / maxPossibleImpact);
  }

  private async initiateHealing(
    componentId: string,
    element: HTMLElement
  ): Promise<boolean> {
    const health = this.componentHealth.get(componentId);
    if (!health || health.status === "healing") return false;

    // Update status
    health.status = "healing";
    health.recoveryAttempts++;

    // Find applicable healing strategies
    const applicableStrategies = this.healingStrategies.filter((strategy) =>
      health.issues.some((issue) => strategy.conditions(issue))
    );

    // Sort by priority
    applicableStrategies.sort((a, b) => a.priority - b.priority);

    let healingSuccess = false;

    for (const strategy of applicableStrategies) {
      try {
        const actions = strategy.actions.map<HealingAction>((action) => ({
          ...action,
          targetComponent: componentId,
        }));

        this.activeHealing.set(componentId, actions);

        // Execute healing actions
        for (const action of actions) {
          const success = await action.implementation();
          action.appliedAt = Date.now();

          if (success) {
            healingSuccess = true;
          } else {
            await action.rollback();
          }
        }

        if (healingSuccess) break;
      } catch {
        // Try the next strategy when one fails.
      }
    }

    // Re-diagnose after healing
    const updatedHealth = await this.diagnoseComponent(
      element,
      componentId,
      health.componentType
    );

    if (healingSuccess && updatedHealth.healthScore > health.healthScore) {
      updatedHealth.status =
        updatedHealth.healthScore > 0.8 ? "healthy" : "warning";
    } else {
      updatedHealth.status = "failed";
    }

    return healingSuccess;
  }

  // Healing implementation methods
  private async resetComponentStyles(): Promise<boolean> {
    try {
      // Implementation would reset component styles
      await new Promise((resolve) =>
        setTimeout(resolve, ANIMATION.DURATION.fast)
      );
      return true;
    } catch {
      return false;
    }
  }

  private async restoreComponentStyles(): Promise<void> {
    // Rollback implementation
    await new Promise((resolve) =>
      setTimeout(resolve, ANIMATION.DURATION.fast / 3)
    );
  }

  private async rebuildComponentDOM(): Promise<boolean> {
    try {
      // Implementation would trigger React re-render
      await new Promise((resolve) =>
        setTimeout(resolve, ANIMATION.DURATION.fast * 1.3)
      );
      return true;
    } catch {
      return false;
    }
  }

  private async restoreComponentDOM(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  private async fixAriaAttributes(): Promise<boolean> {
    try {
      // Implementation would add missing ARIA attributes
      await new Promise((resolve) =>
        setTimeout(resolve, ANIMATION.DURATION.fast / 3)
      );
      return true;
    } catch {
      return false;
    }
  }

  private async removeAriaAttributes(): Promise<void> {
    await new Promise((resolve) =>
      setTimeout(resolve, ANIMATION.DURATION.fast / 6)
    );
  }

  private async optimizePerformance(): Promise<boolean> {
    try {
      // Implementation would apply performance optimizations
      await new Promise((resolve) =>
        setTimeout(resolve, ANIMATION.DURATION.normal * 2)
      );
      return true;
    } catch {
      return false;
    }
  }

  private async revertPerformanceChanges(): Promise<void> {
    await new Promise((resolve) =>
      setTimeout(resolve, ANIMATION.DURATION.normal)
    );
  }

  // Public API
  getComponentHealth(componentId: string): ComponentHealthCheck | undefined {
    return this.componentHealth.get(componentId);
  }

  getAllComponentHealth(): ComponentHealthCheck[] {
    return Array.from(this.componentHealth.values());
  }

  getActiveHealing(): Array<{ componentId: string; actions: HealingAction[] }> {
    return Array.from(this.activeHealing.entries()).map(
      ([componentId, actions]) => ({
        componentId,
        actions,
      })
    );
  }

  recordRenderTime(componentId: string, time: number): void {
    this.perfDetector.recordRenderTime(componentId, time);
  }
}

// React Context for the self-healing system
const SelfHealingContext = createContext<{
  system: SelfHealingSystem | null;
  diagnoseComponent: (
    element: HTMLElement,
    componentId: string,
    componentType: string
  ) => Promise<ComponentHealthCheck>;
  getComponentHealth: (componentId: string) => ComponentHealthCheck | undefined;
  getAllHealth: () => ComponentHealthCheck[];
}>({
  system: null,
  diagnoseComponent: async () => ({}) as ComponentHealthCheck,
  getComponentHealth: () => undefined,
  getAllHealth: () => [],
});

// Provider component
export function GlassSelfHealingProvider({
  children,
  onHealingStarted,
  onHealingCompleted,
  diagnosticInterval = 5000,
}: {
  children: React.ReactNode;
  onHealingStarted?: (componentId: string, issues: ComponentIssue[]) => void;
  onHealingCompleted?: (componentId: string, success: boolean) => void;
  diagnosticInterval?: number;
}) {
  const prefersReducedMotion = useReducedMotion();
  const systemRef = useRef<SelfHealingSystem>();
  const [systemHealth, setSystemHealth] = useState<ComponentHealthCheck[]>([]);

  // Initialize system
  useEffect(() => {
    systemRef.current = new SelfHealingSystem();

    // Periodic health updates
    const updateInterval = setInterval(() => {
      if (systemRef.current) {
        const allHealth = systemRef.current.getAllComponentHealth();
        setSystemHealth(allHealth);
      }
    }, diagnosticInterval);

    return () => clearInterval(updateInterval);
  }, [diagnosticInterval]);

  const diagnoseComponent = useCallback(
    async (
      element: HTMLElement,
      componentId: string,
      componentType: string
    ) => {
      if (!systemRef.current)
        throw new Error("Self-healing system not initialized");
      return systemRef.current.diagnoseComponent(
        element,
        componentId,
        componentType
      );
    },
    []
  );

  const getComponentHealth = useCallback((componentId: string) => {
    return systemRef.current?.getComponentHealth(componentId);
  }, []);

  const getAllHealth = useCallback(() => {
    return systemRef.current?.getAllComponentHealth() || [];
  }, []);

  const value = {
    system: systemRef.current || null,
    diagnoseComponent,
    getComponentHealth,
    getAllHealth,
  };

  return (
    <SelfHealingContext.Provider value={value}>
      {children}
    </SelfHealingContext.Provider>
  );
}

// Hook to use the self-healing system
export function useSelfHealing() {
  const context = useContext(SelfHealingContext);
  if (!context) {
    throw new Error(
      "useSelfHealing must be used within GlassSelfHealingProvider"
    );
  }
  return context;
}

// Self-healing component wrapper
export function GlassSelfHealingWrapper({
  children,
  componentId,
  componentType = "unknown",
  monitoringEnabled = true,
  healingEnabled = true,
  showDebugHud = false,
  className,
}: {
  children: React.ReactNode;
  componentId: string;
  componentType?: string;
  monitoringEnabled?: boolean;
  healingEnabled?: boolean;
  showDebugHud?: boolean;
  className?: string;
}) {
  const prefersReducedMotion = useReducedMotion();
  const { diagnoseComponent, getComponentHealth } = useSelfHealing();
  const elementRef = useRef<HTMLDivElement>(null);
  const [health, setHealth] = useState<ComponentHealthCheck | null>(null);
  const lastDiagnosisRef = useRef<number>(0);

  // Automatic monitoring
  useEffect(() => {
    if (!monitoringEnabled || !elementRef.current) return;

    const runDiagnosis = async () => {
      const now = Date.now();
      if (now - lastDiagnosisRef.current < 3000) return; // Throttle diagnostics

      lastDiagnosisRef.current = now;

      try {
        const diagnosis = await diagnoseComponent(
          elementRef.current!,
          componentId,
          componentType
        );
        setHealth(diagnosis);
      } catch {
        setHealth(null);
      }
    };

    // Initial diagnosis
    runDiagnosis();

    // Periodic monitoring
    const monitorInterval = setInterval(
      runDiagnosis,
      ANIMATION.DURATION.slower * 14
    );

    // Monitor for DOM mutations
    const observer = new MutationObserver(() => {
      runDiagnosis();
    });

    if (elementRef.current) {
      observer.observe(elementRef.current, {
        childList: true,
        attributes: true,
        subtree: true,
      });
    }

    return () => {
      clearInterval(monitorInterval);
      observer.disconnect();
    };
  }, [monitoringEnabled, componentId, componentType, diagnoseComponent]);

  const statusColor = health
    ? {
        healthy: "hsl(var(--glass-color-success))",
        warning: "hsl(var(--glass-color-warning))",
        critical: "hsl(var(--glass-color-danger))",
        healing: "hsl(var(--glass-color-primary))",
        failed: "#7f1d1d",
      }[health.status]
    : "var(--glass-gray-500)";

  return (
    <div
      ref={elementRef}
      className={cn(
        "relative self-healing-wrapper",
        health?.status === "healing" && "self-healing-active",
        className
      )}
      data-component-id={componentId}
      data-component-type={componentType}
      data-health-score={health?.healthScore}
      data-status={health?.status}
    >
      {children}

      {/* Health indicator */}
      {showDebugHud && monitoringEnabled && health && (
        <motion.div
          className="glass-absolute glass-top-1 glass-right-1 glass-w-3 glass-h-3 glass-radius-full glass-border-2 glass-border-white/20"
          style={{ backgroundColor: statusColor }}
          initial={{ scale: 0 }}
          animate={prefersReducedMotion ? {} : { scale: 1 }}
          title={`Health: ${(health.healthScore * 100).toFixed(1)}% | Status: ${health.status} | Issues: ${health.issues.length}`}
        />
      )}
    </div>
  );
}

// System health dashboard
export function GlassSelfHealingDashboard({
  className,
  showOnlyUnhealthy = false,
  defaultOpen = false,
}: {
  className?: string;
  showOnlyUnhealthy?: boolean;
  /** Opens the diagnostic surface on first render; useful for embedded dashboards. */
  defaultOpen?: boolean;
}) {
  const prefersReducedMotion = useReducedMotion();
  const { getAllHealth } = useSelfHealing();
  const [allHealth, setAllHealth] = useState<ComponentHealthCheck[]>([]);
  const [showDashboard, setShowDashboard] = useState(defaultOpen);

  useEffect(() => {
    const updateHealth = () => {
      const health = getAllHealth();
      setAllHealth(
        showOnlyUnhealthy
          ? health.filter((h) => h.status !== "healthy")
          : health
      );
    };

    updateHealth();
    const interval = setInterval(updateHealth, 2000);
    return () => clearInterval(interval);
  }, [getAllHealth, showOnlyUnhealthy]);

  const criticalCount = allHealth.filter((h) => h.status === "critical").length;
  const warningCount = allHealth.filter((h) => h.status === "warning").length;
  const healingCount = allHealth.filter((h) => h.status === "healing").length;
  const dashboard = (
    <AnimatePresence>
      {showDashboard && (
        <motion.div
          className="glass-surface-primary glass-elev-4 glass-radius-lg glass-p-4 glass-gap-3"
          style={{
            position: "fixed",
            zIndex: 51,
            top: "4.75rem",
            left: "1rem",
            width: "min(20rem, calc(100vw - 2rem))",
            maxWidth: "calc(100vw - 2rem)",
            maxHeight: "min(24rem, calc(100vh - 5.75rem))",
            overflowY: "auto",
            color: "var(--glass-theme-text, var(--glass-text-primary))",
            background:
              "linear-gradient(145deg, rgba(255,255,255,.30), rgba(255,255,255,.16))",
            backgroundColor: "rgba(255,255,255,.18)",
            border: "1px solid rgba(255,255,255,.28)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,.28), 0 18px 48px rgba(20,20,20,.14)",
            backdropFilter:
              "blur(24px) saturate(1.4) brightness(1.04) contrast(1.02)",
            WebkitBackdropFilter:
              "blur(24px) saturate(1.4) brightness(1.04) contrast(1.02)",
          }}
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={prefersReducedMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
        >
          <div className="glass-flex glass-items-center glass-justify-between">
            <h3 className="glass-text-sm glass-font-medium glass-text-primary">
              Self-Healing Dashboard
            </h3>
            <button
              onClick={() => setShowDashboard(false)}
              aria-label="Close self-healing dashboard"
              className="glass-text-xs glass-text-secondary hover:glass-text-primary glass-focus glass-touch-target glass-contrast-guard"
            >
              ✕
            </button>
          </div>
          {allHealth.map((health) => (
            <motion.div
              key={health.componentId}
              className="glass-p-3 glass-surface-secondary glass-radius-md"
              initial={{ opacity: 0, x: -10 }}
              animate={prefersReducedMotion ? {} : { opacity: 1, x: 0 }}
            >
              <div className="glass-flex glass-items-center glass-justify-between">
                <span className="glass-text-sm glass-text-primary glass-font-medium">
                  {health.componentType}
                </span>
                <div className="glass-flex glass-items-center glass-gap-2">
                  <div
                    className="glass-w-3 glass-h-3 glass-radius-full"
                    style={{
                      backgroundColor: HEALTH_STATUS_COLORS[health.status],
                    }}
                  />
                  <span className="glass-text-xs glass-text-secondary">
                    {(health.healthScore * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
              {health.issues.length > 0 && (
                <div className="glass-mt-1 glass-text-xs glass-text-tertiary">
                  {health.issues.length} issue
                  {health.issues.length !== 1 ? "s" : ""} detected
                </div>
              )}
            </motion.div>
          ))}
          {allHealth.length === 0 && (
            <div className="glass-text-center glass-text-sm glass-text-secondary glass-py-4">
              All components healthy
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className={cn("fixed top-4 left-4 z-50", className)}>
      <motion.button
        className={cn(
          "w-12 h-12 glass-radius-full glass-surface-primary glass-elev-3",
          "flex items-center justify-center glass-text-primary",
          "transition-all duration-300"
        )}
        onClick={() => setShowDashboard(!showDashboard)}
        aria-label={
          showDashboard
            ? "Close self-healing dashboard"
            : "Open self-healing dashboard"
        }
        aria-expanded={showDashboard}
        whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
        whileTap={{ scale: 0.95 }}
      >
        <span aria-hidden="true" className="glass-text-lg">
          ✦
        </span>
        {criticalCount + warningCount + healingCount > 0 && (
          <motion.div
            className="glass-absolute glass-top-1 glass--right-1 glass-w-3 glass-h-3 glass-surface-red glass-radius-full glass-text-xs glass-text-primary glass-flex glass-items-center glass-justify-center"
            initial={{ scale: 0 }}
            animate={prefersReducedMotion ? {} : { scale: 1 }}
          >
            {criticalCount + warningCount + healingCount}
          </motion.div>
        )}
      </motion.button>

      {typeof document !== "undefined"
        ? createPortal(dashboard, document.body)
        : dashboard}
    </div>
  );
}

// Hook for component self-monitoring
export function useComponentSelfHealing(
  componentId: string,
  componentType: string = "component"
) {
  const { diagnoseComponent } = useSelfHealing();
  const [health, setHealth] = useState<ComponentHealthCheck | null>(null);
  const elementRef = useRef<HTMLElement>(null);

  const runHealthCheck = useCallback(async () => {
    if (elementRef.current) {
      try {
        const diagnosis = await diagnoseComponent(
          elementRef.current,
          componentId,
          componentType
        );
        setHealth(diagnosis);
        return diagnosis;
      } catch {
        return null;
      }
    }
    return null;
  }, [diagnoseComponent, componentId, componentType]);

  return {
    health,
    runHealthCheck,
    elementRef,
    isHealthy: health?.status === "healthy",
    isHealing: health?.status === "healing",
    isCritical: health?.status === "critical",
  };
}

// Main component wrapper for testing and simple usage
export function GlassSelfHealingSystem({
  children,
  className,
  components = [],
  ...props
}: {
  children?: React.ReactNode;
  className?: string;
  components?: SelfHealingComponentInput[];
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <GlassSelfHealingProvider>
      <GlassSelfHealingWrapper
        componentId="test-component"
        componentType="test"
      >
        <div className={className} {...props}>
          {children || <div>Self-Healing System</div>}
        </div>
      </GlassSelfHealingWrapper>
    </GlassSelfHealingProvider>
  );
}

// Presets for different healing configurations
export const selfHealingPresets = {
  strict: {
    monitoringInterval: 2000,
    healingThreshold: 0.9,
    autoHealingEnabled: true,
    visualMonitoring: true,
    accessibilityMonitoring: true,
    performanceMonitoring: true,
  },
  balanced: {
    monitoringInterval: 5000,
    healingThreshold: 0.7,
    autoHealingEnabled: true,
    visualMonitoring: true,
    accessibilityMonitoring: true,
    performanceMonitoring: true,
  },
  relaxed: {
    monitoringInterval: 10000,
    healingThreshold: 0.5,
    autoHealingEnabled: false,
    visualMonitoring: false,
    accessibilityMonitoring: true,
    performanceMonitoring: false,
  },
  development: {
    monitoringInterval: 1000,
    healingThreshold: 0.8,
    autoHealingEnabled: true,
    visualMonitoring: true,
    accessibilityMonitoring: true,
    performanceMonitoring: true,
  },
};
